import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { JobEntity } from '@commenda/api';
import {
  IIndirectTaxFilingV2AggregateParams,
  IndirectTaxFilingV2ListExtend,
  SalesTaxFilingFrequencyEnum,
  useIndirectTaxFilingV2StatusCounts,
  useIndirectTaxFilingsAggregate,
  useIndirectTaxFilingsV2,
} from '@commenda/api/indirect-tax/filings';
import { useIndirectTaxRegistrations } from '@commenda/api/indirect-tax/registrations';
import { useServiceRequestsPaginated } from '@commenda/api/service-request';
import { TaskParty } from '@commenda/prisma';

import { IndirectTaxRegion, IndirectTaxRegionHelper } from '../../common';
import { INDIRECT_TAX_TABLE_IDS } from '../../common/constants/tableIds';
import { IndirectTaxClientTaskHelper } from '../../common/helpers';
import { useIndirectTaxFilingAccess } from '../../common/hooks/useIndirectTaxFilingAccess';
import { useIndirectTaxJobWatcher } from '../../common/hooks/useIndirectTaxJobWatcher';
import { useIndirectTaxRegionScope } from '../../common/hooks/useIndirectTaxRegionScope';
import { IndirectTaxClientTask } from '../../common/types';
import {
  buildKey,
  useRegionScopedSessionState,
  useSessionPersistedState,
} from '../../common/utils/filterStorage';

import {
  IndirectTaxFilingDashboardHelper,
  IndirectTaxFilingListRowHelper,
  IndirectTaxFilingsHelper,
} from '../helpers';
import { IndirectTaxFilingStatus } from '../types';

const PAGE_SIZE = 100;

interface UseIndirectTaxFilingsDashboardParams {
  corporationId: number;
  taskAssigneeType?: TaskParty;
}

export function useIndirectTaxFilingsDashboard({
  corporationId,
  taskAssigneeType = TaskParty.CLIENT,
}: UseIndirectTaxFilingsDashboardParams) {
  const T = INDIRECT_TAX_TABLE_IDS.FILINGS;

  const [activeRegion, setActiveRegion] =
    useSessionPersistedState<IndirectTaxRegion>(
      buildKey(T, 'region', corporationId),
      IndirectTaxRegion.ALL_REGIONS
    );
  const [activeFilingStatusTab, setActiveFilingStatusTab] =
    useSessionPersistedState<IndirectTaxFilingStatus | 'ALL'>(
      buildKey(T, 'status-tab', corporationId),
      'ALL'
    );

  const [activeFilingFilters, setActiveFilingFilters] =
    useRegionScopedSessionState<Record<string, string | string[]>>(
      buildKey(T, 'filters', corporationId),
      activeRegion
    );

  const [filingPeriod, setFilingPeriod] = useSessionPersistedState<{
    fromDate: string;
    toDate: string;
  }>(
    buildKey(T, 'period', corporationId),
    IndirectTaxFilingDashboardHelper.buildInitialFilingPeriod()
  );

  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  const resetPagination = useCallback(() => {
    setCurrentCursor(null);
    setCursorHistory([]);
  }, []);

  const { isInternalUser, isCalendarOnly } =
    useIndirectTaxFilingAccess(activeRegion);
  const isAllRegions = activeRegion === IndirectTaxRegion.ALL_REGIONS;
  const {
    jurisdictions: regionJurisdictions,
    jurisdictionIds: regionJurisdictionIds,
    isResolved: isRegionResolved,
    isUnavailable: isRegionUnavailable,
    isLoading: isLoadingRegion,
    error: regionError,
    retry: retryRegion,
  } = useIndirectTaxRegionScope({ corporationId, region: activeRegion });

  // Drives the empty-state CTA ("Generate calendar" vs "Go to registrations").
  // Scoped to the active region via jurisdiction IDs so a corp with EU
  // registrations only sees "Go to registrations" on the US tab.
  const {
    total: regionRegistrationCount,
    isLoading: isLoadingRegistrationCount,
    error: registrationCountError,
    mutate: mutateRegistrationCount,
  } = useIndirectTaxRegistrations(
    isRegionResolved && (isAllRegions || regionJurisdictionIds.length > 0)
      ? {
          filters: {
            corporationId,
            jurisdictionIds: isAllRegions ? undefined : regionJurisdictionIds,
            limit: 1,
          },
        }
      : null
  );

  const hasRegistrations = (regionRegistrationCount ?? 0) > 0;

  // Table-filter-driven jurisdiction override. The filter stores jurisdiction
  // ids directly, so it can flow straight to the API.
  const selectedJurisdictionIds = useMemo(
    () =>
      IndirectTaxFilingDashboardHelper.getJurisdictionFilterIds(
        activeFilingFilters
      ),
    [activeFilingFilters]
  );

  const effectiveJurisdictionIds = useMemo(
    () =>
      IndirectTaxFilingDashboardHelper.computeEffectiveJurisdictionIds(
        selectedJurisdictionIds,
        isAllRegions,
        regionJurisdictionIds
      ),
    [selectedJurisdictionIds, isAllRegions, regionJurisdictionIds]
  );

  const selectedFrequencies = useMemo(() => {
    const raw =
      IndirectTaxFilingDashboardHelper.getFrequencyFilterValues(
        activeFilingFilters
      );
    if (!raw) return undefined;
    // Frequencies come from a typed filter with SalesTaxFilingFrequencyEnum
    // values; the filter API strips that specificity to string.
    return raw as SalesTaxFilingFrequencyEnum[];
  }, [activeFilingFilters]);

  const effectiveStatuses = useMemo(
    () =>
      IndirectTaxFilingDashboardHelper.computeEffectiveStatuses(
        activeFilingStatusTab
      ),
    [activeFilingStatusTab]
  );

  const filingsEnabled =
    isRegionResolved &&
    (effectiveJurisdictionIds === undefined ||
      effectiveJurisdictionIds.length > 0);

  // Contribution regions derive amount-due from filing_contribution rows, so
  // opt into the extend on those tabs. The ALL_REGIONS view also needs it
  // because its row set can include contribution-region filings (see
  // toListRowForVAT / toListRowForCanada). Filing-row-driven tabs render
  // from filing amount fields and never need the extend.
  const listExtend: IndirectTaxFilingV2ListExtend[] | undefined = useMemo(
    () =>
      !isCalendarOnly &&
      (IndirectTaxRegionHelper.isContributionRegion(activeRegion) ||
        activeRegion === IndirectTaxRegion.ALL_REGIONS)
        ? [IndirectTaxFilingV2ListExtend.FILING_CONTRIBUTION]
        : undefined,
    [activeRegion, isCalendarOnly]
  );

  const {
    filings: rawFilings,
    count,
    nextCursor,
    mutate: mutateFilings,
    error: filingsError,
    isLoading: isLoadingFilings,
  } = useIndirectTaxFilingsV2(
    filingsEnabled
      ? {
          corporationId,
          limit: PAGE_SIZE,
          cursor: currentCursor ?? undefined,
          fromDueDate: filingPeriod.fromDate,
          toDueDate: filingPeriod.toDate,
          jurisdictionIds: effectiveJurisdictionIds,
          filingFrequencies: selectedFrequencies,
          filingStatuses: effectiveStatuses,
          includeServiceRequest: true,
          includeFiledByAgent: true,
          includeAssignedUser: true,
          extend: listExtend,
        }
      : null,
    { keepPreviousData: false }
  );

  // Aggregate-summary call backing the dashboard summary cards. Strictly
  // single-region: ALL_REGIONS tab opts out (the backend refuses
  // cross-region aggregation; the UI hides the cards in that case — see
  // IndirectTaxFilingsTable). Filter set matches the list endpoint exactly
  // minus pagination, so cards re-aggregate when the user changes status
  // tab / period / jurisdiction / frequency.
  const aggregateParams =
    useMemo<IIndirectTaxFilingV2AggregateParams | null>(() => {
      if (!filingsEnabled || isCalendarOnly) return null;
      if (!IndirectTaxFilingsHelper.supportsAggregate(activeRegion))
        return null;
      const aggregateRegion =
        IndirectTaxFilingsHelper.toAggregateRegion(activeRegion);
      if (aggregateRegion === null) return null;
      return {
        corporationId,
        region: aggregateRegion,
        fromDueDate: filingPeriod.fromDate,
        toDueDate: filingPeriod.toDate,
        jurisdictionIds: effectiveJurisdictionIds,
        filingFrequencies: selectedFrequencies,
        filingStatuses: effectiveStatuses,
      };
    }, [
      filingsEnabled,
      isCalendarOnly,
      activeRegion,
      corporationId,
      filingPeriod.fromDate,
      filingPeriod.toDate,
      effectiveJurisdictionIds,
      selectedFrequencies,
      effectiveStatuses,
    ]);

  // `aggregate` is null while the first request is in flight (SWR's
  // keepPreviousData keeps the previous cards rendered across filter
  // changes, so an explicit loading flag isn't needed). `aggregateError`
  // is surfaced to the dashboard component as a non-blocking toast.
  const { aggregate, error: aggregateError } =
    useIndirectTaxFilingsAggregate(aggregateParams);

  // Per-status counts for the tab labels. Fires 5 parallel count-only list
  // calls (limit=1), pinned to the same non-status filters as the main list.
  // Status tab switching never invalidates these — they refresh only when
  // region/period/jurisdiction/frequency change.
  const { counts: statusCounts, isLoading: isLoadingStatusCounts } =
    useIndirectTaxFilingV2StatusCounts(
      filingsEnabled
        ? {
            corporationId,
            fromDueDate: filingPeriod.fromDate,
            toDueDate: filingPeriod.toDate,
            jurisdictionIds: effectiveJurisdictionIds,
            filingFrequencies: selectedFrequencies,
          }
        : null
    );

  const { isJobInProgress, getJobsByEntityAndOperation } =
    useIndirectTaxJobWatcher({
      corporation_id: corporationId,
    });

  const isFilingJobInProgress = isJobInProgress(JobEntity.FILING);

  const prevJobInProgressRef = useRef(false);
  useEffect(() => {
    if (prevJobInProgressRef.current && !isFilingJobInProgress) {
      mutateFilings?.();
    }
    prevJobInProgressRef.current = isFilingJobInProgress;
  }, [isFilingJobInProgress, mutateFilings]);

  const filings = useMemo(
    () =>
      (rawFilings ?? []).map((filing) =>
        IndirectTaxFilingListRowHelper.toListRowForViewer(
          filing,
          isInternalUser
        )
      ),
    [rawFilings, isInternalUser]
  );

  // Sticky flag so that once we've seen filings for the corporation (in the
  // list or in any status count), tab / filter combos that return zero rows
  // still render the tabs + empty table instead of the empty state.
  const hasEverHadFilingsRef = useRef(false);
  if (
    IndirectTaxFilingDashboardHelper.hasAnyFilings(
      count,
      filings.length,
      statusCounts
    )
  ) {
    hasEverHadFilingsRef.current = true;
  }

  const hasFilings = hasEverHadFilingsRef.current;

  // Client-task data for the top-of-page task bar. Previously lived inline in
  // the page component; hooks own data fetching + derivation so the page only
  // orchestrates layout.
  const { data: filingServiceRequests } = useServiceRequestsPaginated({
    corporationId,
    includeServiceTypes: ['SALES_TAX_FILING'],
    includedStatuses:
      IndirectTaxClientTaskHelper.getActiveFilingServiceRequestStatuses(),
  });

  const clientTasks = useMemo<IndirectTaxClientTask[]>(
    () =>
      IndirectTaxClientTaskHelper.deriveFilingClientTasks(
        filingServiceRequests ?? [],
        taskAssigneeType,
        isInternalUser
      ),
    [filingServiceRequests, taskAssigneeType, isInternalUser]
  );

  const handleFilingRegionChange = useCallback(
    (region: IndirectTaxRegion) => {
      setActiveRegion(region);
      setActiveFilingStatusTab('ALL');
      resetPagination();
    },
    [resetPagination]
  );

  const handleFilingStatusTabChange = useCallback(
    (tab: IndirectTaxFilingStatus | 'ALL') => {
      setActiveFilingStatusTab(tab);
      setActiveFilingFilters({});
      resetPagination();
    },
    [resetPagination]
  );

  const handleFilingFilterChange = useCallback(
    (filters: Record<string, string | string[]>) => {
      setActiveFilingFilters(filters);
      resetPagination();
    },
    [resetPagination]
  );

  const handleFilingPeriodChange = useCallback(
    (fromDate: string, toDate: string) => {
      setFilingPeriod({ fromDate, toDate });
      resetPagination();
    },
    [resetPagination]
  );

  const handleNextPage = useCallback(() => {
    if (!nextCursor) return;
    setCursorHistory((prev) =>
      IndirectTaxFilingDashboardHelper.pushCursorHistory(prev, currentCursor)
    );
    setCurrentCursor(nextCursor);
  }, [nextCursor, currentCursor]);

  const handlePreviousPage = useCallback(() => {
    setCursorHistory((prev) => {
      const { history, cursor } =
        IndirectTaxFilingDashboardHelper.popCursorHistory(prev);
      setCurrentCursor(cursor);
      return history;
    });
  }, []);

  // DataTable's cursor paginator expects a zero-indexed page (start = currentPage * pageSize + 1).
  const currentPage = cursorHistory.length;
  const hasPreviousPage = cursorHistory.length > 0;
  const hasNextPage = !!nextCursor;

  const retryFilings = useCallback(async () => {
    await Promise.all([
      retryRegion(),
      mutateFilings(),
      mutateRegistrationCount(),
    ]);
  }, [retryRegion, mutateFilings, mutateRegistrationCount]);

  return {
    isRegionUnavailable,
    activeRegion,
    activeFilingStatusTab,
    activeFilingFilters,
    filingPeriod,
    filings,
    regionJurisdictions,
    totalFilingCount: count,
    statusCounts,
    hasFilings,
    hasRegistrations,
    // Wait for status counts before falling back to the empty state, so a
    // zero-row status tab doesn't flash "Filings will appear here".
    isLoadingFilings:
      isLoadingFilings ||
      isLoadingRegion ||
      isLoadingRegistrationCount ||
      (isLoadingStatusCounts && !hasFilings),
    filingsError: filingsError ?? regionError ?? registrationCountError,
    isFilingJobInProgress,
    clientTasks,
    aggregate,
    aggregateError,
    handleFilingRegionChange,
    handleFilingStatusTabChange,
    handleFilingFilterChange,
    handleFilingPeriodChange,
    handleNextPage,
    handlePreviousPage,
    hasNextPage,
    hasPreviousPage,
    currentPage,
    pageSize: PAGE_SIZE,
    getJobsByEntityAndOperation,
    mutateFilings,
    retryFilings,
  };
}
